import { useState } from "react";
import { toast } from "sonner";
import { DadosIrradiacao } from "@/types";
import { 
  fetchCepData, 
  fetchIrradiationData 
} from "../utils/geocodingUtils";

export const useIrradiationSearch = (meses: string[], onDadosCarregados: (dados: DadosIrradiacao) => void) => {
  const [loading, setLoading] = useState(false);
  const [dadosIrradiacao, setDadosIrradiacao] = useState<DadosIrradiacao | null>(null);
  const [cepSearchInProgress, setCepSearchInProgress] = useState(false);
  const [cepSearchCompleted, setCepSearchCompleted] = useState(false);
  const [searchStep, setSearchStep] = useState<'initial' | 'cep-found' | 'complete'>('initial');
  const [usingDefaultHSP, setUsingDefaultHSP] = useState(false);

  const handleCepSearch = async (cep: string) => {
    setCepSearchInProgress(true);
    
    try {
      const data = await fetchCepData(cep);
      
      if (data) {
        setCepSearchCompleted(true);
        setSearchStep('cep-found');
        return data;
      }
    } catch (error) {
      console.error("Error fetching CEP data:", error);
      toast.error("Erro ao buscar o CEP");
    } finally {
      setCepSearchInProgress(false);
    }
    
    return null;
  };

  const handleFullSearch = async (cep: string) => {
    setLoading(true);
    setDadosIrradiacao(null);
    setCepSearchCompleted(false);
    setSearchStep('initial');
    
    try {
      // Step 1: Fetch address data from CEP
      const addressData = await fetchCepData(cep);
      
      if (!addressData) {
        setLoading(false);
        return;
      }
      
      setCepSearchCompleted(true);
      
      // Latitude and longitude for Brazil's center as fallback
      const latitude = -15.77972;
      const longitude = -47.92972;
      
      // Step 2: Fetch irradiation data
      const irradiationData = await fetchIrradiationData(cep, latitude, longitude, meses);
      
      if (irradiationData) {
        setDadosIrradiacao(irradiationData);
        setSearchStep('complete');
        toast.success("Dados de irradiação carregados com sucesso!");
        onDadosCarregados(irradiationData);
      }
    } catch (error) {
      console.error("Error in full search process:", error);
      toast.error("Erro ao processar o CEP informado.");
    } finally {
      setLoading(false);
    }
  };

  const useDefaultHSP = () => {
    // Create default HSP data with value 4.80
    const defaultHSP = 4.80;
    
    const dadosMensaisSimulados = meses.map(mes => ({
      mes,
      valor: defaultHSP + (Math.random() * 0.4 - 0.2) // Small variation around 4.80
    }));
    
    const defaultIrradiacao: DadosIrradiacao = {
      cep: "00000000",
      latitude: -15.77972,
      longitude: -47.92972,
      irradiacaoMedia: defaultHSP,
      minIrradiacao: defaultHSP - 0.5,
      maxIrradiacao: defaultHSP + 0.5,
      mesMin: "Junho",
      mesMax: "Dezembro",
      dadosMensais: dadosMensaisSimulados
    };
    
    setDadosIrradiacao(defaultIrradiacao);
    setUsingDefaultHSP(true);
    setSearchStep('complete');
    toast.success("Dados de irradiação padrão (4,80 kWh/m²/dia) carregados com sucesso!");
    onDadosCarregados(defaultIrradiacao);
  };
  
  return {
    loading,
    dadosIrradiacao,
    cepSearchInProgress,
    cepSearchCompleted,
    searchStep,
    usingDefaultHSP,
    handleCepSearch,
    handleFullSearch,
    useDefaultHSP
  };
};
